import { Controller } from '@hotwired/stimulus'
import { FetchRequest } from '@rails/request.js'

export default class extends Controller {
  static values = {
    truckloadOrderId: Number,
    customerName: String,
    customerEmail: String,
    customerPhone: String,
    customerState: String,
    customerBusinessType: String,
    customerUnloadingOptions: Array,
    refreshTableUrl: String,
  }

  static targets = [
    "EmailBrokersContainer", "EmailForm",
    "EmailCustomerContainer", "EmailCustomerForm", "SkipEmailingInput",
    "CreateOrderFormContainer", "CreateOrderFormStep1", "CreateOrderForm", "CreateOrderFormButton", "OrderQuantityInput", "OrderListingIdInput", "OrderListingDescription", "OrderTruckloadOrderQuantityInput", "OrderShippingIncludedInput",
    "AddRowForm",
    "DeliveredPriceEmailSubject", "DeliveredPriceEmailBody",
    "OrderTruckloadOrderBulkAmountContainer", "OrderTruckloadOrderBulkAmount50kInput", "OrderTruckloadOrderBulkAmountFullInput",
    "OrderTruckloadOrderToteSizeContainer", "OrderTruckloadOrderToteSize2kInput", "OrderTruckloadOrderToteSize40BushelInput",
    "AddRowAutomatedContainer", "AddRowManualContainer", "AddRowAutomatedTrueInput", "AddRowTruckTypeInput"
  ]

  get truckload_rates_table_controller() {
    return document.querySelector(`[data-controller="truckload-rates-table"]`)[`truckload-rates-table`]
  }

  connect() {
    this.element[this.identifier] = this

    this.showingDeliveredPriceEmailForm = false
    this.showingCreateOrderForm = false
    this.showingEmailRateCheckboxes = false
    this.selectedRateShippingAmount = 0
    this.selectedRateBulkOrTote = null
    this.selectedOrderRateId = null
    this.sendingFreshRates = false
    this.singleLoadQuantityInLbs = null
  }

  onEmailBrokers(e) {
    this.EmailBrokersContainerTarget.classList.remove("hidden")
  }

  onCancelEmailBrokers(e) {
    this.EmailBrokersContainerTarget.classList.add("hidden")
  }

  async onSendEmailToBrokers(e) {
    await this.EmailFormTargets.map(async (form) => {
      const formData = new FormData(form)

      if (this.SkipEmailingInputTarget.checked) {
        formData.append("truckload_rate_extra[skip_emailing]", true)
      }

      const request = new FetchRequest('post', form.action, { body: formData, responseKind: 'turbo-stream' })
      const response = await request.perform()

      if (response.ok) {
        //  do something with response
      }
    })

    this.onCancelEmailBrokers(e)
  }

  onEmailCustomer() {
    this.showingDeliveredPriceEmailForm = true
    this.EmailCustomerContainerTarget.classList.remove("hidden")
    this.showingCreateOrderForm = false
    this.CreateOrderFormContainerTarget.classList.add("hidden")
    this.truckload_rates_table_controller.showEmailCheckboxes()
    this.showingEmailRateCheckboxes = true
  }

  onCancelEmailCustomer() {
    this.showingDeliveredPriceEmailForm = false
    this.EmailCustomerContainerTarget.classList.add("hidden")
    this.truckload_rates_table_controller.hideEmailCheckboxes()
    this.showingEmailRateCheckboxes = false
  }

  async onSendEmailToCustomer(e) {
    let formData = new FormData(this.EmailCustomerFormTarget)
    formData.append('sending_fresh_rates', this.sendingFreshRates)
    const request = new FetchRequest('post', this.EmailCustomerFormTarget.action, { body: formData, responseKind: 'turbo-stream' })
    await request.perform()

    this.onCancelEmailCustomer()
  }

  onCreateOrderClick(e) {
    e.preventDefault()

    this.CreateOrderFormContainerTarget.classList.remove("hidden")
    this.showingCreateOrderForm = true
    this.truckload_rates_table_controller.showEmailCheckboxes()
    this.truckload_rates_table_controller.clearEmailCheckboxes()
    this.showingEmailRateCheckboxes = true

    if (this.showingDeliveredPriceEmailForm) {
      this.onCancelEmailCustomer(e)
    }
  }

  onCancelCreateOrderClick(e) {
    e.preventDefault()

    this.CreateOrderFormContainerTarget.classList.add("hidden")
    this.showingCreateOrderForm = false
    this.truckload_rates_table_controller.hideEmailCheckboxes()
    this.showingEmailRateCheckboxes = false
    this.refreshTruckloadRatesTable()
  }

  async refreshTruckloadRatesTable() {
    const request = new FetchRequest('get', this.refreshTableUrlValue, { responseKind: 'turbo-stream' })
    const response = await request.perform()

    if (response.ok) {
      //  do something with response
    }
  }

  async onRateCheckboxChange(e) {
    if (e.target.checked) {
      if (this.showingCreateOrderForm) {
        this.truckload_rates_table_controller.clearOtherSelectedRateCheckboxes(e)
        this.onRateChecked(e.target.dataset.truckloadRateId)
      }
    } else {
      this.onRateUnchecked()
    }
  }

  async onRateChecked(truckloadRateId) {
    this.CreateOrderFormStep1Target.classList.add("hidden")
    this.CreateOrderFormTarget.classList.remove("hidden")
    this.CreateOrderFormButtonTarget.classList.remove("hidden")

    // autopopulate form data
    const request = new FetchRequest('get', `/admin/truckload_rates/${truckloadRateId}`)
    const response = await request.perform()

    if (response.ok) {
      const truckloadRate = await response.json
      this.OrderQuantityInputTarget.value = (parseInt(truckloadRate.truckload_order.quantity) * parseInt(truckloadRate.number_of_lbs_customer_for_truck_type))
      this.singleLoadQuantityInLbs = this.OrderQuantityInputTarget.value
      this.OrderListingIdInputTarget.value = truckloadRate.listing_id
      this.OrderListingDescriptionTarget.innerHTML = truckloadRate.listing.cms_title
      this.selectedRateShippingAmount = truckloadRate.rate_in_cents / 1000.00
      this.selectedOrderRateId = truckloadRate.id
      if (truckloadRate.truck_type == "Hopper Bottom") {
        this.OrderTruckloadOrderBulkAmountContainerTarget.classList.remove("hidden")
        this.OrderTruckloadOrderToteSizeContainerTarget.classList.add("hidden")
        this.selectedRateBulkOrTote = 'Bulk'
      } else {
        this.OrderTruckloadOrderBulkAmountContainerTarget.classList.add("hidden")
        this.OrderTruckloadOrderToteSizeContainerTarget.classList.remove("hidden")
        this.selectedRateBulkOrTote = 'Tote'
      }

      if (truckloadRate.truck_type == "Tarped Flatbed") {
        this.OrderTruckloadOrderToteSize40BushelInputTarget.checked = true
      } else if (truckloadRate.truck_type == "Dry Van") {
        this.OrderTruckloadOrderToteSize2kInputTarget.checked = true
      }
    }
  }

  onRateUnchecked() {
    this.CreateOrderFormStep1Target.classList.remove("hidden")
    this.CreateOrderFormTarget.classList.add("hidden")
    this.CreateOrderFormButtonTarget.classList.add("hidden")
  }

  onTruckloadOrderQuantityChange(e) {
    this.OrderQuantityInputTarget.value = (parseInt(e.currentTarget.value) * parseInt(this.singleLoadQuantityInLbs))
  }

  async onCreateOrderFormSubmit(e) {
    e.preventDefault()

    let orderFormData = new FormData()
    orderFormData.append("order[truckload_order_id]", this.truckloadOrderIdValue)
    orderFormData.append("order[name]", this.customerNameValue)
    orderFormData.append("order[email]", this.customerEmailValue)
    orderFormData.append("order[email_confirmation]", this.customerEmailValue)
    orderFormData.append("order[phone_number]", this.customerPhoneValue)
    orderFormData.append("order[billing_state]", this.customerStateValue)
    orderFormData.append("order[shipping_state]", this.customerStateValue)
    orderFormData.append("order[business_type]", this.customerBusinessTypeValue.includes("Farm") ? "farm" : "business")
    orderFormData.append("order[loading_option]", this.customerUnloadingOptionsValue.includes("Dock") ? "loading_dock" : this.customerUnloadingOptionsValue.includes("Forklift") ? "forklift" : "liftgate")
    orderFormData.append("order[truckload_data][truckload_order_quantity]", this.OrderTruckloadOrderQuantityInputTarget.value)
    orderFormData.append("order[selected_truckload_rate_id]", this.selectedOrderRateId)
    if (this.selectedRateBulkOrTote == 'Bulk') {
      orderFormData.append("order[truckload_data][truckload_order_bulk_amount]", this.OrderTruckloadOrderBulkAmount50kInputTarget.checked ? "50k" : "full")
    } else {
      orderFormData.append("order[truckload_data][truckload_order_tote_size]", this.OrderTruckloadOrderToteSize2kInputTarget.checked ? "2000" : "2240")
    }
    const shippingIncluded = this.OrderShippingIncludedInputTarget.checked
    const singleLoadShippingAmount = this.selectedRateShippingAmount
    let shippingAmount

    if (shippingIncluded) {
      shippingAmount = this.OrderTruckloadOrderQuantityInputTarget.value * singleLoadShippingAmount
      orderFormData.append("order[shipping]", shippingAmount)
    } else {
      orderFormData.append("order[truckload_data][truckload_order_estimated_shipping]", singleLoadShippingAmount)
    }

    let request = new FetchRequest('post', "/admin/orders", { body: orderFormData, responseKind: 'json' })
    let response = await request.perform()

    if (response.ok) {
      let order = await response.json

      let orderItemData = new FormData()
      orderItemData.append("order_item[order_id]", order.id)
      orderItemData.append("order_item[quantity]", this.OrderQuantityInputTarget.value)
      orderItemData.append("order_item[listing_id]", this.OrderListingIdInputTarget.value)
      orderItemData.append("order_item[via_cms]", true)

      request = new FetchRequest('post', `/admin/order_items`, { body: orderItemData, responseKind: 'json' })
      response = await request.perform()

      if (response.ok) {
        let orderItem = await response.json
        navigator.clipboard.writeText(orderItem.order.payment_intent_url)
        window.location = `/admin/orders/${orderItem.order.id}`
      }
    } else {
      alert(await response.text)
    }
  }

  async onStatusChange(e) {
    const formData = new FormData(e.currentTarget.form)

    const request = new FetchRequest('patch', e.currentTarget.dataset.url, { body: formData, responseKind: 'json' })
    const response = await request.perform()

    if (response.ok) {
      //  do something with response
    }
  }

  async onLeadChange(e) {
    const formData = new FormData(e.currentTarget.form)
    formData.append("truckload_order[lead]", e.currentTarget.checked)

    const request = new FetchRequest('patch', e.currentTarget.dataset.url, { body: formData, responseKind: 'json' })
    const response = await request.perform()

    if (response.ok) {
      //  do something with response
    }
  }

  onAddRow() {
    this.AddRowFormTarget.classList.toggle("hidden")
  }

  async onFreshPriceEmailChange(e) {
    const checked = e.currentTarget.checked
    let deliveredPriceEmailUrl = e.currentTarget.dataset.deliveredPriceEmailUrl

    if (checked) {
      this.sendingFreshRates = true
      deliveredPriceEmailUrl += '?fresh_price_email=true'
    } else {
      this.sendingFreshRates = false
    }

    const request = new FetchRequest('get', deliveredPriceEmailUrl, { responseKind: 'json' })
    const response = await request.perform()

    if (response.ok) {
      let responseJson = await response.json

      this.DeliveredPriceEmailSubjectTarget.value = responseJson.subject
      this.DeliveredPriceEmailBodyTarget.value = responseJson.body
    }
  }

  onAddRowBrokerChange(e) {
    if (e.currentTarget.value == "Thorstenson Trucking") {
      this.AddRowAutomatedContainerTarget.classList.remove("hidden")
      this.AddRowAutomatedTrueInputTarget.checked = true
      this.AddRowManualContainerTarget.classList.add("hidden")
      this.AddRowTruckTypeInputTarget.value = "Hopper Bottom"
    } else {
      this.AddRowAutomatedContainerTarget.classList.add("hidden")
      this.AddRowManualContainerTarget.classList.remove("hidden")
    }
  }

  onAddRowAutomatedChange(e) {
    if (e.currentTarget.value == "true") {
      this.AddRowManualContainerTarget.classList.add("hidden")
    } else {
      this.AddRowManualContainerTarget.classList.remove("hidden")
    }
  }

}